import { Controller } from "@hotwired/stimulus"

/**
 * Generic form controller for has_one tax organizer associations
 * Works with: address, filer_info, contact_info, refund_payment_details
 *
 * Usage:
 *   data-controller="tax-organizer--association-form"
 *   data-tax-organizer--association-form-update-url-value="/path"
 *   data-tax-organizer--association-form-question-id-value="123"
 *
 *   Fields:
 *   data-tax-organizer--association-form-target="field"
 *   data-action="blur->tax-organizer--association-form#autoSave"
 */
export default class extends Controller {
  static targets = ["field"]
  static outlets = ["tax-organizer--scroll-position", "tax-organizer--progress"]

  static values = {
    updateUrl: String,
    questionId: String
  }

  connect() {
    this.isSaving = false
    this.pendingSaveValues = null
    this.lastSavedValues = this.currentValues

    // Bind and register the morph protection handler
    this.boundMorphHandler = this.preventMorphingActiveInput.bind(this)
    document.addEventListener('turbo:before-morph-element', this.boundMorphHandler)
  }

  disconnect() {
    // Clean up the event listener
    document.removeEventListener('turbo:before-morph-element', this.boundMorphHandler)
  }

  // Prevent morphing of inputs that have been modified by the user
  // This handles race conditions where an out-of-order response would clobber user input
  preventMorphingActiveInput(event) {
    const morphTarget = event.target
    const newElement = event.detail.newElement

    // Only care about inputs within this controller's scope
    if (!this.element.contains(morphTarget)) return

    // Only care about form inputs
    const isFormInput = morphTarget.tagName === 'INPUT' ||
                        morphTarget.tagName === 'TEXTAREA' ||
                        morphTarget.tagName === 'SELECT'
    if (!isFormInput) return

    // If there's no new element (removal), allow it
    if (!newElement) return

    // Get current DOM value and incoming server value
    const currentValue = morphTarget.type === 'checkbox' ? morphTarget.checked : morphTarget.value
    const newValue = newElement.type === 'checkbox' ? newElement.checked : newElement.value

    // If values differ, the user has modified this input since the request was sent
    // Preserve the user's value by preventing the morph
    if (currentValue !== newValue) {
      // Sync validation classes from the new element
      // (error styling should update even if value is preserved)
      if (newElement.classList.contains('is-invalid')) {
        morphTarget.classList.add('is-invalid')
      } else {
        morphTarget.classList.remove('is-invalid')
      }

      event.preventDefault()
      return
    }

    // If this is the focused element, prevent morph to avoid cursor position issues
    // (even if values are the same, morphing can reset cursor)
    if (document.activeElement === morphTarget) {
      event.preventDefault()
    }
  }

  get currentValues() {
    const values = {}
    this.fieldTargets.forEach(field => {
      // Use name as key, handle checkboxes specially
      if (field.type === 'checkbox') {
        values[field.name] = field.checked ? '1' : '0'
      } else {
        values[field.name] = field.value.trim()
      }
    })
    return values
  }

  get hasChanges() {
    const current = this.currentValues

    // Compare each field value
    for (const [name, value] of Object.entries(current)) {
      if (this.lastSavedValues[name] !== value) {
        return true
      }
    }
    return false
  }

  // Auto-save on blur
  async autoSave(event) {
    // Get current values for change detection and later saving
    const currentValues = this.currentValues

    if (!this.hasChanges) return

    // If a save is already in progress, queue this save for later
    // This handles rapid successive changes (e.g., selecting account type then immediately checking a checkbox)
    if (this.isSaving) {
      this.pendingSaveValues = currentValues
      return
    }

    // Perform the save
    await this.performSave(currentValues)
  }

  // Perform the actual save operation
  async performSave(currentValues) {
    try {
      this.isSaving = true
      this.pendingSaveValues = null
      this.element.setAttribute('data-saving', 'true')

      // Remember scroll position
      if (this.hasTaxOrganizerScrollPositionOutlet) {
        this.taxOrganizerScrollPositionOutlet.rememberFromElement(this.element)
      }

      // Show spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.showQuestionSaving(this.questionIdValue)
      }

      // Build form data from the captured values (not from DOM)
      const formData = new FormData()
      for (const [name, value] of Object.entries(currentValues)) {
        formData.append(name, value)
      }

      // Make PATCH request
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })

      // Process Turbo Stream response
      if (response.ok || response.status === 422) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Update last saved values to the values we just saved (not current DOM values)
        // This prevents a race where DOM changes during save would be incorrectly marked as "already saved"
        // Do this for both success (200) and validation errors (422)
        this.lastSavedValues = currentValues

        // Clear validation errors on successful save (when skip_question_frame is used)
        // On 422, the question frame is morphed with errors, so nothing to clear
        if (response.ok) {
          this.clearValidationErrors()
        }
      }

      // Hide spinner on question badge
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }

      this.isSaving = false
      this.element.removeAttribute('data-saving')

      // Check if there's a pending save queued while we were saving
      if (this.pendingSaveValues) {
        const pendingValues = this.pendingSaveValues
        this.pendingSaveValues = null
        // Trigger the pending save with the stored values
        this.performSave(pendingValues)
      }
    } catch (error) {
      console.error('Auto-save failed:', error)
      this.isSaving = false
      this.element.removeAttribute('data-saving')

      // Hide spinner on error
      if (this.hasQuestionIdValue && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(this.questionIdValue)
      }

      // Clear any pending save on error
      this.pendingSaveValues = null
    }
  }

  // Clear validation errors from all fields
  clearValidationErrors() {
    // Remove is-invalid class from all fields
    this.fieldTargets.forEach(field => {
      // Check if this field delegates validation display to another element
      // (e.g., masked inputs where the hidden field holds the value but the visible input shows validation)
      const validationElementId = field.dataset.validationElementId
      const validationElement = validationElementId
        ? document.getElementById(validationElementId)
        : field

      validationElement?.classList.remove('is-invalid')
    })

    // Remove all invalid-feedback elements within this form
    this.element.querySelectorAll('.invalid-feedback').forEach(feedback => {
      feedback.remove()
    })
  }

  // Helper to get CSRF token
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
